'use client';

import { useMergeRefs } from '@floating-ui/react';
import { forwardRef, useMemo, useRef } from 'react';

import { css, cx } from '@/styled-system/css';

import { useDisabledInteractions } from '../../hooks/use-disabled-interactions';
import { useInteractiveElementKeyDownCallback } from '../../hooks/use-interactive-element-key-down-callback';
import { Slot } from '../slot';
import { cardRootRecipe } from './styles/card-root.styles';
import type { CardRootPropsInterface } from './types/card-root.types';

/**
 * @since 10.0.0
 */
export const CardRoot = forwardRef<HTMLDivElement, CardRootPropsInterface>(
  (props: CardRootPropsInterface, forwardedRef) => {
    const {
      asChild = false,
      outlined = true,
      className,
      css: cssProp,
      disabled = false,
      elevated = true,
      interactive = false,
      role,
      tone = 'neutral',
      onClick,
      onKeyDown,
      ...rest
    } = props;

    const cardRef = useRef<HTMLDivElement>(null);
    const refs = useMergeRefs([cardRef, forwardedRef]);

    const styles = css(
      cardRootRecipe.raw({ outlined, disabled, elevated, interactive, tone }),
      cssProp,
    );

    const Component = asChild ? Slot : 'div';

    const onKeyDownCallback = useInteractiveElementKeyDownCallback({
      ref: cardRef,
      onClick,
      onKeyDown,
    });

    const isInteractive = interactive && (onClick || onKeyDown);

    const resolvedRole = asChild ? role : (role ?? 'group');

    // Since card elements don't support the `disabled` attribute,
    // we need to override any event interactions
    const disabledInteractionsProps = useDisabledInteractions({ focusable: false });

    const interactiveProps = useMemo(
      () =>
        isInteractive
          ? {
              onClick,
              onKeyDown: onKeyDownCallback,
              role: 'button',
              tabIndex: 0,
            }
          : {},
      [isInteractive, onClick, onKeyDownCallback],
    );

    return (
      <Component
        ref={refs}
        data-c11n-component="Card.Root"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', 'c11n_card__root', styles, className)}
        role={resolvedRole}
        {...interactiveProps}
        {...rest}
        {...(isInteractive && disabled ? disabledInteractionsProps : {})}
      />
    );
  },
);

CardRoot.displayName = 'Card.Root';
